import { motion } from "framer-motion";
import { FadeUp } from "./Reveal";

const AppleIcon = () => (
  <svg viewBox="0 0 384 512" fill="currentColor" className="h-6 w-6">
    <path d="M318.7 268.7c-.2-36.7 16.4-64.4 50-84.8-18.8-26.9-47.2-41.7-84.7-44.6-35.5-2.8-74.3 20.7-88.5 20.7-15 0-49.4-19.7-76.4-19.7C63.3 141.2 4 184.8 4 273.5q0 39.3 14.4 81.2c12.8 36.7 59 126.7 107.2 125.2 25.2-.6 43-17.9 75.8-17.9 31.8 0 48.3 17.9 76.4 17.9 48.6-.7 90.4-82.5 102.6-119.3-65.2-30.7-61.7-90-61.7-91.9zm-56.6-164.2c27.3-32.4 24.8-61.9 24-72.5-24.1 1.4-52 16.4-67.9 34.9-17.5 19.8-27.8 44.3-25.6 71.9 26.1 2 49.9-11.4 69.5-34.3z" />
  </svg>
);

const PlayIcon = () => (
  <svg viewBox="0 0 512 512" fill="currentColor" className="h-6 w-6">
    <path d="M325.3 234.3L104.6 13l280.8 161.2-60.1 60.1zM47 0C34 6.8 25.3 19.2 25.3 35.3v441.3c0 16.1 8.7 28.5 21.7 35.3l256.6-256L47 0zm425.2 225.6l-58.9-34.1-65.7 64.5 65.7 64.5 60.1-34.1c18-14.3 18-46.5-1.2-60.8zM104.6 499l280.8-161.2-60.1-60.1L104.6 499z" />
  </svg>
);

export default function AppDownload() {
  return (
    <section
      id="app"
      data-testid="app-download-section"
      className="mx-auto max-w-7xl scroll-mt-28 px-6 py-32 lg:px-10 lg:py-40"
    >
      <div className="grid items-center gap-16 lg:grid-cols-12 lg:gap-20">
        <div className="lg:col-span-6">
          <FadeUp>
            <p className="text-xs font-bold uppercase tracking-[0.3em] text-[#C85C40]">
              The Spintastic App
            </p>
            <h2 className="mt-4 font-serif text-4xl leading-none tracking-tight sm:text-5xl lg:text-6xl">
              Your laundromat,{" "}
              <span className="italic text-[#C85C40]">in your pocket</span>.
            </h2>
            <p className="mt-6 max-w-lg text-base leading-relaxed text-[#5C5C5A] md:text-lg">
              Schedule pickups, track your order, pay, and manage every load from
              your phone. Our Android app is live on Google Play today — and the
              iOS version is putting on its final polish.
            </p>

            <div className="mt-10 flex flex-wrap gap-4">
              <div
                data-testid="app-store-badge"
                className="inline-flex cursor-pointer items-center gap-3 rounded-full bg-[#1A1A1A] px-6 py-3.5 text-white transition-transform duration-300 hover:scale-[0.98]"
              >
                <AppleIcon />
                <span className="text-left leading-tight">
                  <span className="block text-[10px] uppercase tracking-widest opacity-60">
                    Coming soon to the
                  </span>
                  <span className="block text-sm font-bold">App Store</span>
                </span>
              </div>
              <a
                href="https://play.google.com/store/apps/details?id=com.starchup.spintasticcity"
                target="_blank"
                rel="noopener noreferrer"
                data-testid="google-play-badge"
                className="inline-flex cursor-pointer items-center gap-3 rounded-full bg-[#1A1A1A] px-6 py-3.5 text-white transition-transform duration-300 hover:scale-[0.98]"
              >
                <PlayIcon />
                <span className="text-left leading-tight">
                  <span className="block text-[10px] uppercase tracking-widest opacity-60">
                    Get it on
                  </span>
                  <span className="block text-sm font-bold">Google Play</span>
                </span>
              </a>
            </div>
          </FadeUp>
        </div>

        <div className="lg:col-span-6">
          <FadeUp delay={0.15} className="relative flex justify-center">
            <div
              aria-hidden="true"
              className="absolute top-1/2 left-1/2 h-80 w-80 -translate-x-1/2 -translate-y-1/2 rounded-full bg-[#7D8C74]/15 blur-2xl"
            />
            <motion.div
              animate={{ y: [0, -12, 0] }}
              transition={{ duration: 5, repeat: Infinity, ease: "easeInOut" }}
              className="relative w-64 sm:w-72"
              data-testid="app-mockup-image"
            >
              <div className="overflow-hidden rounded-[3rem] border-[10px] border-[#1A1A1A] bg-[#1A1A1A] shadow-[0_24px_64px_rgba(0,0,0,0.18)]">
                <div className="flex aspect-[9/19] flex-col items-center justify-center gap-6 bg-[#F9F6F0] p-6">
                  <img
                    src="/assets/logo-full.png"
                    alt="Spintastic City Laundry Services app splash screen with logo"
                    className="w-full rounded-2xl"
                  />
                  <div className="flex items-center gap-2" aria-hidden="true">
                    {[0, 1, 2].map((i) => (
                      <motion.span
                        key={i}
                        className="h-2 w-2 rounded-full bg-[#C85C40]"
                        animate={{ opacity: [0.25, 1, 0.25] }}
                        transition={{
                          duration: 1.4,
                          repeat: Infinity,
                          delay: i * 0.2,
                          ease: "easeInOut",
                        }}
                      />
                    ))}
                  </div>
                  <p className="text-[10px] font-bold uppercase tracking-[0.3em] text-[#5C5C5A]">
                    Opening soon
                  </p>
                </div>
              </div>
            </motion.div>
          </FadeUp>
        </div>
      </div>
    </section>
  );
}
